

import { useState, useEffect } from 'react';
import Pagination from "../components/Pagination.jsx";
import { motion } from 'framer-motion';
import { Trash2, Edit, Plus, RefreshCw, Image, AlertCircle, AlertTriangle, Check } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { DeleteBanner, UpateBannerHomePage, UplaodBannerHomePage, GetAllBanner } from '../components/Api.jsx';
// Image component removed - using regular img tags

export default function BannerManager() {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);

  const [banners, setBanners] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false); // State for popup
  const [refreshing, setRefreshing] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', onConfirm: null });

  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm({
    defaultValues: { heading: '', text: '', sequence: 1 },
  });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const fetchBanners = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setIsLoading(true);
      }
      setError(null);
      const response = await GetAllBanner();
      console.log('Raw API response:', response); // Debug: Log raw response

      // Handle different response structures
      let bannerData = [];
      let status;

      if (Array.isArray(response?.data?.banners)) {
        bannerData = response.data.banners;
        status = response.data.status;
      } else if (Array.isArray(response?.banners)) {
        bannerData = response.banners;
        status = response.status;
      } else if (Array.isArray(response)) {
        bannerData = response;
      } else {
        throw new Error(`Invalid response format: ${JSON.stringify(response, null, 2)}`);
      }

      // Validate status (case-insensitive, optional)
      if (status && status.toLowerCase() !== 'success') {
        throw new Error(`Unexpected status: ${status}`);
      }

      // Check for duplicate sequences
      const sequences = bannerData.map(b => b.sequence);
      if (new Set(sequences).size !== sequences.length) {
        setError('Warning: Duplicate sequence numbers detected, which may affect display order.');
      }

      setBanners(bannerData);
      console.log('Updated banners state:', bannerData); // Debug: Log banners state
    } catch (error) {
      setError(`Failed to fetch banners: ${error.message}`);
      console.error('Error fetching banners:', error);
    } finally {
      if (isRefresh) {
        setRefreshing(false);
      } else {
        setIsLoading(false);
      }
    }
  };

  const handleRefresh = () => {
    fetchBanners(true);
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const onSubmit = async (data) => {
    try {
      setError(null);
      const file = data.file?.[0];
      if (editingId) {
        await UpateBannerHomePage(editingId, data.heading, data.text, data.sequence, file);
        showToast('Banner updated successfully');
      } else {
        if (!file) throw new Error('Image is required for new banners');
        await UplaodBannerHomePage(data.heading, data.text, data.sequence, file);
        showToast('Banner created successfully');
      }
      reset();
      setEditingId(null);
      setIsPopupOpen(false); // Close popup after submission
      fetchBanners();
    } catch (error) {
      console.error('Error submitting banner:', error);
      showToast('Failed to save banner. Please try again.', 'error');
    }
  };

  const handleEdit = (banner) => {
    setEditingId(banner.id);
    setValue('heading', banner.heading);
    setValue('text', banner.text);
    setValue('sequence', banner.sequence);
    setIsPopupOpen(true); // Open popup for editing
  };

  const handleDelete = (bannerId) => {
    setConfirmModal({
      show: true,
      title: 'Confirm Delete',
      message: 'Are you sure you want to delete this banner? This action cannot be undone.',
      onConfirm: async () => {
        try {
          setError(null);
          await DeleteBanner(bannerId);
          showToast('Banner deleted successfully');
          fetchBanners();
        } catch (error) {
          console.error('Error deleting banner:', error);
          showToast('Failed to delete banner. Please try again.', 'error');
        } finally {
          setConfirmModal(prev => ({ ...prev, show: false }));
        }
      }
    });
  };

  const openPopup = () => {
    setEditingId(null);
    reset(); // Reset form for new entry
    setIsPopupOpen(true);
  };

  return (
    <div className="min-h-screen bg-white text-black container p-4">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
          }`}>
          <div className="flex items-center gap-2">
            {toast.type === 'error' ? <AlertCircle className="w-5 h-5" /> : <Check className="w-5 h-5" />}
            {toast.message}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col lg:flex-row justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-start gap-4">
                <div className="bg-pink-100 p-3 rounded-lg">
                  <Image className="w-6 h-6 text-pink-600" />
                </div>
                <div>
                  <p className="text-gray-600 text-sm leading-relaxed max-w-3xl">
                    This section contains all the universal banners displayed in the application to the users. Manage banner content, images, and display order.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg transition-all duration-200 disabled:cursor-not-allowed text-sm font-medium shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                aria-label={refreshing ? "Refreshing data" : "Refresh banners"}
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
              </button>
              <button
                onClick={openPopup}
                className="bg-blue-600 text-white px-4 py-2 flex items-center gap-2 hover:bg-blue-700"
              >
                <Plus size={20} />
                Add New Banner
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error Message with Retry Button */}
      {error && (
        <div className="bg-red-600 text-white p-4 rounded mb-4 flex justify-between">
          <span>{error}</span>
          <button
            onClick={fetchBanners}
            className="bg-white text-red-600 px-2 py-1 rounded"
          >
            Retry
          </button>
        </div>
      )}

      {/* Popup Form */}
      {isPopupOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md"
          >
            <h2 className="text-2xl font-bold mb-4">{editingId ? 'Edit Banner' : 'Add Banner'}</h2>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="heading" className="block text-sm font-medium text-gray-700">
                    Banner Heading
                  </label>
                  <input
                    id="heading"
                    {...register('heading', { required: 'Heading is required' })}
                    placeholder="Banner Heading"
                    className="w-full p-2 rounded bg-gray-100 text-black border border-gray-300"
                  />
                  {errors.heading && (
                    <p className="text-red-600 text-sm mt-1">{errors.heading.message}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="sequence" className="block text-sm font-medium text-gray-700">
                    Sequence
                  </label>
                  <input
                    id="sequence"
                    {...register('sequence', {
                      required: 'Sequence is required',
                      valueAsNumber: true,
                      min: { value: 1, message: 'Sequence must be at least 1' },
                      validate: (value) => {
                        const isDuplicate = banners.some(
                          (banner) => banner.sequence === value && banner.id !== editingId
                        );
                        return isDuplicate ? 'This Sequence number already exists' : true;
                      }
                    })}
                    type="number"
                    placeholder="Sequence"
                    className="w-full p-2 rounded bg-gray-100 text-black border border-gray-300"
                  />
                  {errors.sequence && (
                    <p className="text-red-600 text-sm mt-1">{errors.sequence.message}</p>
                  )}
                </div>
              </div>
              <div>
                <label htmlFor="text" className="block text-sm font-medium text-gray-700">
                  Banner Text
                </label>
                <textarea
                  id="text"
                  {...register('text', { required: 'Text is required' })}
                  placeholder="Banner Text"
                  className="w-full p-2 rounded bg-gray-100 text-black border border-gray-300"
                  rows={3}
                />
                {errors.text && (
                  <p className="text-red-600 text-sm mt-1">{errors.text.message}</p>
                )}
              </div>
              <div>
                <label htmlFor="file" className="block text-sm font-medium text-gray-700">
                  Banner Image {editingId ? '(Optional)' : '(Required)'}
                </label>
                <input
                  id="file"
                  {...register('file', { required: !editingId && 'Image is required' })}
                  type="file"
                  accept="image/*"
                  className="w-full p-2 rounded bg-gray-100 text-black border border-gray-300"
                />
                {errors.file && (
                  <p className="text-red-600 text-sm mt-1">{errors.file.message}</p>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsPopupOpen(false)}
                  className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded flex items-center gap-2"
                >
                  <Plus size={20} />
                  {editingId ? 'Update Banner' : 'Add Banner'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Banner List */}
      {isLoading ? (
        <div className="text-center py-10">Loading banners...</div>
      ) : banners.length === 0 ? (
        <p className="text-gray-600 text-center">No banners found</p>
      ) : (
        <div className="grid gap-6">
          {banners
            .sort((a, b) => a.sequence - b.sequence)
            .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
            .map((banner, index) => (
              <motion.div
                key={banner.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-gray-100 rounded-lg p-6 flex items-center justify-between"
              >
                <div className="flex items-center gap-4">
                  {banner.imageUrl && (
                    <img
                      src={banner.imageUrl}
                      alt={`Banner image for ${banner.heading}`}
                      className="w-24 h-24 object-cover rounded"
                      width={96}
                      height={96}
                      onError={(e) => {
                        e.currentTarget.src = '/placeholder-image.png';
                      }}
                    />
                  )}
                  <div>
                    <h3 className="text-xl font-semibold">{banner.heading}</h3>
                    <p className="text-gray-700">{banner.text}</p>
                    <p className="text-sm text-gray-500">Sequence: {banner.sequence}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(banner)}
                    className="p-2 text-blue-600 hover:text-blue-500"
                    title="Edit banner"
                    aria-label={`Edit banner ${banner.heading}`}
                  >
                    <Edit size={20} />
                  </button>
                  <button
                    onClick={() => handleDelete(banner.id)}
                    className="p-2 text-red-600 hover:text-red-500"
                    title="Delete banner"
                    aria-label={`Delete banner ${banner.heading}`}
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </motion.div>
            ))}
        </div>
      )}

      {/* Custom Confirm Modal */}
      {confirmModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999] p-4 backdrop-blur-sm transition-all duration-300">
          <div className="bg-white rounded-2xl max-w-sm w-full shadow-2xl transform transition-all duration-300 scale-100 p-6 border border-gray-100">
            <div className="flex flex-col items-center text-center">
              <div className="bg-red-50 p-4 rounded-full mb-4">
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {confirmModal.title}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-8">
                {confirmModal.message}
              </p>
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setConfirmModal(prev => ({ ...prev, show: false }))}
                  className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-colors font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmModal.onConfirm}
                  className="flex-1 px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-xl shadow-lg shadow-red-200 transition-all duration-200 font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    
      <div className="mt-6">
        <Pagination 
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={Math.ceil((Array.isArray(banners) ? banners.length : (banners?.length || 0)) / itemsPerPage)}
          totalElements={(Array.isArray(banners) ? banners.length : (banners?.length || 0))}
          pageSize={itemsPerPage}
          setPageSize={setItemsPerPage}
        />
      </div>

    </div>
);
}